import React from 'react';
import './WelcomeMessage.css';
import { AuthenticationProps } from '../hooks/auth';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CodeIcon from '@mui/icons-material/Code';
import HelpIcon from '@mui/icons-material/Help';
import { DOCS } from '../constants/externalLinks';

interface WelcomeMessageProps {
  authContext: AuthenticationProps;
}

interface UserName {
  fullName: string;
  firstName: string;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ authContext }) => {
  // Get user's email from profile
  const userEmail =
    authContext.profile?.['https://claims.cimpress.io/email'] || '';

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Extract name from email with multiple format options
  const getUserName = (email: string): UserName | string => {
    if (!email) return 'there';

    const namePart = email.split('@')[0];

    // Option 1: Full name with proper capitalization
    const fullName = namePart
      .split('.')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Option 2: First name only (current implementation)
    const firstName = namePart.split('.')[0];
    const firstNameFormatted =
      firstName.charAt(0).toUpperCase() + firstName.slice(1);

    // Return both formats
    return {
      fullName,
      firstName: firstNameFormatted,
    };
  };

  const userName = authContext.profile?.given_name
    ? authContext.profile?.given_name
    : getUserName(userEmail);
  const greeting = getTimeBasedGreeting();

  return (
    <div className="welcome-message">
      <h2>
        {greeting} {typeof userName === 'string' ? userName : userName.fullName}
        ! 👋
      </h2>
      <p>
        To get started, please select a Semantic Model from the dropdown menu in
        buttom right corner.
      </p>
      <p>
        Once you've selected a dataset, you can start asking questions about
        your data.
      </p>

      <div className="features-section">
        <h3>Key Features</h3>
        <div className="feature-grid">
          <div className="feature-item">
            <LightbulbIcon className="feature-icon" />
            <div className="feature-content">
              <h4>Smart Insights</h4>
              <p>Get intelligent analysis and insights from your data</p>
            </div>
          </div>

          <div className="feature-item">
            <CodeIcon className="feature-icon" />
            <div className="feature-content">
              <h4>SQL Generation</h4>
              <p>
                Generates only SQL queries from natural language and does not
                execute the query
              </p>
            </div>
          </div>

          <div className="feature-item">
            <HelpIcon className="feature-icon" />
            <div className="feature-content">
              <h4>Documentation</h4>
              <p>
                <a
                  href={DOCS.CHATMCP}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more about ChatMCP
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessage;
